// procurement rollup — turns the per-vessel SHEET 27 order data into the cumulative figures the
// Procurement department needs: how many boats need each option / engine / colour (a tally per column,
// like the totals row of the yard's Excel), and every vessel's additional requests in one list.

import { ORDER_SHEET_COLUMNS, editDistance } from '@technohull/shared';

export interface ProcVessel {
  sap: string;
  model: string;
  name: string;
  /** storage key for this vessel's stored cells — defaults to its SAP, but an imported-only boat
   *  (with no/unknown SAP) is keyed synthetically, so reads must go through the key. */
  key?: string;
}
/** where this vessel's stored order-sheet cells live (its key, falling back to SAP). */
const rowKey = (v: ProcVessel): string => v.key ?? v.sap;
/** sap -> { column letter: cell value } — the shape returned by /api/order-sheet. */
export type SheetData = Record<string, Record<string, string>>;

// the additional-requests block (a marker column + AR1…AR10) is free text, so it's handled separately
// from the option tally.
const AR_MARKER_KEY = 'CV';
const AR_ITEM_KEYS = ['CW', 'CX', 'CY', 'CZ', 'DA', 'DB', 'DC', 'DD', 'DE', 'DF'];
const AR_ALL_KEYS = new Set([AR_MARKER_KEY, ...AR_ITEM_KEYS]);

// the yard uses '.', '-' and '_' purely as visual separators between sections — treat them (and blanks)
// as "no value" so they don't pollute the counts.
const SEPARATORS = new Set(['', '.', '-', '_']);
export const isBlankCell = (v: string | undefined | null): boolean => !v || SEPARATORS.has(v.trim());

/** every spec column except the additional-requests block — the columns worth tallying. */
export const OPTION_COLUMNS = ORDER_SHEET_COLUMNS.filter((c) => !AR_ALL_KEYS.has(c.key));
/** just AR1…AR10 (the free-text request slots). */
export const AR_COLUMNS = ORDER_SHEET_COLUMNS.filter((c) => AR_ITEM_KEYS.includes(c.key));

// section a column belongs to, for grouping the rollup the way the sheet is laid out. Each entry is the
// column letter a section starts at; a column takes the last section whose start is at or before it.
const COL_INDEX = new Map(ORDER_SHEET_COLUMNS.map((c, i) => [c.key, i]));
const SECTION_STARTS: ReadonlyArray<readonly [string, string]> = [
  ['D', 'Setup & layout'],
  ['J', 'Colours & finish'],
  ['Q', 'Engines'],
  ['X', 'Engine peripherals'],
  ['AG', 'Instruments & electronics'],
  ['AZ', 'Deck & cockpit'],
  ['BU', 'Electric equipment'],
  ['CN', 'Accessories'],
  ['DG', 'Extra screens'],
];
export function sectionForColumn(key: string): string {
  const idx = COL_INDEX.get(key) ?? -1;
  let label = 'Other';
  for (const [startKey, name] of SECTION_STARTS) {
    if (idx >= (COL_INDEX.get(startKey) ?? Infinity)) label = name;
    else break;
  }
  return label;
}

/** a distinct value in a column, with how many vessels chose it and — for the drill-down — which ones. */
export interface OptionValue {
  value: string;
  count: number;
  /** the vessels that chose this value, so hovering/clicking a count reveals exactly which boats. */
  vessels: ProcVessel[];
}

export interface OptionTally {
  key: string;
  label: string;
  /** the layout section this option belongs to (for grouping). */
  section: string;
  /** number of vessels with a real (non-separator) value in this column. */
  total: number;
  /** distinct values and how many vessels chose each, most common first. */
  values: OptionValue[];
  /** true when every value is just the "+" selected-marker (so the total says it all). */
  plainMark: boolean;
}

/** count each option column across the given vessels. Columns with no real value anywhere are dropped
 *  (that hides the separator/blank columns automatically), and column order is preserved so related
 *  options stay together the way the sheet groups them. */
export function buildTally(vessels: ProcVessel[], sheet: SheetData): OptionTally[] {
  const out: OptionTally[] = [];
  for (const col of OPTION_COLUMNS) {
    const groups = new Map<string, ProcVessel[]>();
    let total = 0;
    for (const v of vessels) {
      const raw = sheet[rowKey(v)]?.[col.key];
      if (isBlankCell(raw)) continue;
      const val = raw!.trim();
      const g = groups.get(val);
      if (g) g.push(v);
      else groups.set(val, [v]);
      total += 1;
    }
    if (total === 0) continue;
    const values: OptionValue[] = [...groups.entries()]
      .map(([value, vs]) => ({ value, count: vs.length, vessels: vs }))
      .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
    out.push({ key: col.key, label: col.label, section: sectionForColumn(col.key), total, values, plainMark: values.every((x) => x.value === '+') });
  }
  return out;
}

export interface VesselRequests {
  sap: string;
  model: string;
  name: string;
  requests: string[];
}

/** every vessel that has at least one additional request, with those requests collected in order. */
export function buildAdditionalRequests(vessels: ProcVessel[], sheet: SheetData): VesselRequests[] {
  const out: VesselRequests[] = [];
  for (const v of vessels) {
    const data = sheet[rowKey(v)] ?? {};
    const requests = AR_COLUMNS
      .map((c) => data[c.key])
      .filter((x): x is string => !isBlankCell(x))
      .map((x) => x.trim());
    if (requests.length) out.push({ sap: v.sap, model: v.model, name: v.name, requests });
  }
  return out;
}

// ── Seakeepers ───────────────────────────────────────────────────────────────────────────────────────
// SHEET 27 has no Seakeeper column: the gyro stabiliser gets written in by hand, mostly as an additional
// request, and spelled every which way: "SEAKEEPER 3", "Sea Keeper", "seekeeper", "2x seakeepers",
// "Σικίπερ". So every cell of a boat is searched with a forgiving match. A boat counts once, or the
// number written in front of it ("2x", "2 pcs"); a number right after it is the model ("Seakeeper 3").

const GREEK_TO_LATIN: Record<string, string> = {
  α: 'a', β: 'v', γ: 'g', δ: 'd', ε: 'e', ζ: 'z', η: 'i', θ: 'th', ι: 'i', κ: 'k', λ: 'l', μ: 'm',
  ν: 'n', ξ: 'x', ο: 'o', π: 'p', ρ: 'r', σ: 's', ς: 's', τ: 't', υ: 'y', φ: 'f', χ: 'ch', ψ: 'ps', ω: 'o',
};

/** a piece of text as plain lower-case Latin words and numbers: accents dropped, Greek spelled out
 *  ("Σικίπερ" → "sikiper"), "2x" and "×2" split into "2" "x" (a Greek Χ beside a number is that x too). */
function words(text: string): string[] {
  const plain = text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[×*]/g, ' x ')
    .replace(/(\d)\s*χ(?!\p{L})/gu, '$1 x ').replace(/(?<!\p{L})χ\s*(\d)/gu, ' x $1');
  const latin = Array.from(plain, (ch) => GREEK_TO_LATIN[ch] ?? ch).join('');
  return latin.match(/[a-z]+|\d+(?:[.,]\d+)?/g) ?? [];
}

// sea / see / si + keep / kip + er, however the vowels, a doubled letter or a plural come out
const SEAKEEPER_SHAPE = /^s[aeiy]{1,3}k{1,2}[aeiy]{1,3}p{1,2}[aeio]?rs?$/;

/** one word (or two run together, "sea keeper") that reads as Seakeeper. */
export function isSeakeeperWord(w: string): boolean {
  if (SEAKEEPER_SHAPE.test(w)) return true;
  // a slip of the keys on the full name ("seakeeepr", "saekeeper", "seakeeoer")
  return w.startsWith('s') && w.length >= 7 && w.length <= 11 &&
    Math.min(editDistance(w, 'seakeeper', 2), editDistance(w, 'seakeepers', 2)) <= 2;
}

const NEGATION = new Set(['no', 'not', 'without', 'except', 'excl', 'excluding', 'minus', 'choris', 'xoris', 'ochi', 'oxi', 'den']);
const CANCELLED = /^(cancel|akyr|akir)/;
// a no written after it, ending the request ("SEAKEEPER - NO", "Seakeeper ΟΧΙ")
const TRAILING_NO = new Set(['no', 'ochi', 'oxi']);
const saysNoAfter = (w: readonly string[], end: number) => {
  const rest = w.slice(end + 1).filter((x) => !/^\d/.test(x));
  return rest.length === 1 && TRAILING_NO.has(rest[0]!);
};
const PIECES = new Set(['pc', 'pcs', 'piece', 'pieces', 'unit', 'units', 'tem', 'temachia', 'temaxia', 'temachio', 'tmch']);
const isCount = (w: string | undefined) => !!w && /^[1-9]$/.test(w);
const isNumber = (w: string | undefined) => !!w && /^\d+(?:[.,]\d+)?$/.test(w) && Number(w.replace(',', '.')) <= 100;

export interface SeakeeperFind {
  /** how many: a number written in front ("2x", "2 pcs") or after ("x2"), else 1. */
  qty: number;
  /** the model written after the name ("3", "10.5", "Ride"), or '' when none is given. */
  model: string;
}

/** every Seakeeper a piece of text asks for. A mention that says no ("no seakeeper", "χωρίς Seakeeper",
 *  "seakeeper cancelled") doesn't count. */
export function findSeakeepers(text: string): SeakeeperFind[] {
  const out: SeakeeperFind[] = [];
  // a comma, a semicolon or a new line ends one request; "w/o" is "without"
  for (const clause of text.replace(/\bw\/o\b/gi, ' without ').split(/[,;\n|&+]/)) {
    const w = words(clause);
    for (let i = 0; i < w.length; i++) {
      let end = i;
      if (!isSeakeeperWord(w[i]!)) {
        if (i + 1 < w.length && /^[a-z]+$/.test(w[i]!) && /^[a-z]+$/.test(w[i + 1]!) && isSeakeeperWord(w[i]! + w[i + 1]!)) end = i + 1;
        else continue;
      }
      const before = w.slice(Math.max(0, i - 2), i);
      const after = w.slice(end + 1, end + 3);
      if (before.some((b) => NEGATION.has(b)) || (before.at(-2) === 'w' && before.at(-1) === 'o') || after.some((a) => CANCELLED.test(a)) || saysNoAfter(w, end)) {
        i = end;
        continue;
      }
      let qty = 0;
      let model = '';
      // in front: "2x seakeeper", "2 seakeepers", "2 pcs seakeeper"
      const [b2, b1] = [w[i - 2], w[i - 1]];
      if (b1 === 'x' && isCount(b2)) qty = Number(b2);
      else if (isCount(b1)) qty = Number(b1);
      else if (b1 && PIECES.has(b1) && isCount(b2)) qty = Number(b2);
      // after: "seakeeper x2", "seakeeper 3", "seakeeper model 6 x2", "seakeeper 2 pcs", "seakeeper ride"
      let j = end + 1;
      if (w[j] === 'model' || w[j] === 'mod' || w[j] === 'type') j++;
      if (w[j] === 'x' && isCount(w[j + 1])) { qty ||= Number(w[j + 1]); }
      else if (isNumber(w[j])) {
        if (PIECES.has(w[j + 1] ?? '') && isCount(w[j])) qty ||= Number(w[j]);
        else {
          model = w[j]!.replace(',', '.');
          if (w[j + 1] === 'x' && isCount(w[j + 2])) qty ||= Number(w[j + 2]);
          // "seakeeper 3 (2 pcs)"
          else if (isCount(w[j + 1]) && PIECES.has(w[j + 2] ?? '')) qty ||= Number(w[j + 1]);
        }
      } else if (w[j] === 'ride') model = 'Ride';
      out.push({ qty: qty || 1, model });
      i = end;
    }
  }
  return out;
}

/** where on a boat's sheet something was written (a Seakeeper, a Watch-it). */
export interface WrittenMention {
  /** the column letter and its name, e.g. CX / "AR2". */
  key: string;
  label: string;
  /** the cell as written. */
  text: string;
}

export interface WrittenVessel extends ProcVessel {
  qty: number;
  /** the model(s) written, '' when none is given. */
  models: string[];
  mentions: WrittenMention[];
}

export interface WrittenCount {
  /** units to order: each boat's count added up. */
  units: number;
  vessels: WrittenVessel[];
  /** units by model ('' = not given), most first. */
  byModel: Array<{ model: string; units: number }>;
}

/** what the given boats ask for, from every cell of their order sheet, as `find` reads a cell. A boat that
 *  names it in more than one cell (AR1 "Seakeeper 3", AR4 "Seakeeper bracket") still counts once, or by the
 *  largest number written. */
export function countWritten(vessels: ProcVessel[], sheet: SheetData, find: (text: string) => SeakeeperFind[]): WrittenCount {
  const found: WrittenVessel[] = [];
  for (const v of vessels) {
    const data = sheet[rowKey(v)] ?? {};
    let qty = 0;
    const models = new Set<string>();
    const mentions: WrittenMention[] = [];
    for (const col of ORDER_SHEET_COLUMNS) {
      const raw = data[col.key];
      if (isBlankCell(raw)) continue;
      const finds = find(raw!);
      if (!finds.length) continue;
      mentions.push({ key: col.key, label: col.label.replace(/:$/, ''), text: raw!.trim() });
      for (const f of finds) {
        qty = Math.max(qty, f.qty);
        if (f.model) models.add(f.model);
      }
    }
    if (mentions.length) found.push({ ...v, qty, models: [...models].sort(), mentions });
  }
  const byModel = new Map<string, number>();
  for (const v of found) {
    const m = v.models.join(' / ');
    byModel.set(m, (byModel.get(m) ?? 0) + v.qty);
  }
  return {
    units: found.reduce((n, v) => n + v.qty, 0),
    vessels: found,
    byModel: [...byModel.entries()]
      .map(([model, units]) => ({ model, units }))
      .sort((a, b) => b.units - a.units || (a.model === '' ? 1 : b.model === '' ? -1 : a.model.localeCompare(b.model, undefined, { numeric: true }))),
  };
}

export type SeakeeperMention = WrittenMention;
export type SeakeeperVessel = WrittenVessel;
export type SeakeeperCount = WrittenCount;

/** the Seakeepers the given boats need (countWritten with findSeakeepers). */
export const countSeakeepers = (vessels: ProcVessel[], sheet: SheetData): SeakeeperCount => countWritten(vessels, sheet, findSeakeepers);

// ── Watch-it anti-collision ──────────────────────────────────────────────────────────────────────────
// Like the Seakeeper, the Watch-it anti-collision system has no column on SHEET 27 and is written in by
// hand every which way: "Watch-it", "WATCH IT", "watchit", "Whatch-it", "Watch-it anti collision",
// "anti-collision system", "anticolision", "collision avoidance", "Γουότς ιτ", "σύστημα αντισύγκρουσης".
// A boat takes one, so any of those anywhere on its sheet counts the boat once, whatever number is written.

// watch + it, run together however it was typed: watchit, wachit, whatchit, watcit, wotchit, watsit
const WATCHIT_SHAPE = /^wh?[ao]+(?:tch|ch|tsh|ts|tc|c)h?[iey]t{1,2}s?$/;
// the Greek spelled out: "Γουότσιτ" / "Γουότς ιτ" → goyotsit
const WATCHIT_GREEK = /^g[ouy]{1,4}t?s[iy]t$/;
// collision, however the doubled letters come out: colision, collission, colission, collition
const COLLISION_SHAPE = /^c+o+l+[iy]+[szt]+[iy]*o+n+s?$/;
// σύγκρουση / αντισύγκρουσης / αντισυγκρουστικό, spelled out: sygkroysi, antisygkroysis, antisygkroystiko
const COLLISION_GREEK = /^(?:anti)?s[iyu]n?g{1,2}k?r[o]?[uy]?s/;

/** a word that reads as "collision" (or "anticollision" run together), in English or Greek. */
export function isCollisionWord(w: string): boolean {
  const rest = w.startsWith('anti') && w.length > 8 ? w.slice(4) : w;
  if (COLLISION_SHAPE.test(rest) || COLLISION_GREEK.test(w)) return true;
  // a slip of the keys ("colisoin", "collsion")
  return rest.startsWith('col') && rest.length >= 7 && rest.length <= 11 && editDistance(rest, 'collision', 2) <= 2;
}

/** how many words from `i` read as Watch-it (1 or 2: "watch it"), else 0. */
function watchItAt(w: readonly string[], i: number): number {
  const one = w[i]!;
  if (WATCHIT_SHAPE.test(one) || WATCHIT_GREEK.test(one)) return 1;
  const two = w[i + 1];
  if (two && /^[a-z]+$/.test(one) && /^[a-z]+$/.test(two) && (WATCHIT_SHAPE.test(one + two) || WATCHIT_GREEK.test(one + two))) return 2;
  return 0;
}

/** whether a piece of text asks for the Watch-it anti-collision system. A mention that says no ("no
 *  Watch-it", "χωρίς αντισύγκρουσης", "watch-it cancelled") doesn't count; a boat takes one, so a number
 *  written with it ("2x watch-it") still counts once. */
export function findWatchIts(text: string): SeakeeperFind[] {
  for (const clause of text.replace(/\bw\/o\b/gi, ' without ').split(/[,;\n|&+]/)) {
    const w = words(clause);
    for (let i = 0; i < w.length; i++) {
      const n = watchItAt(w, i) || (isCollisionWord(w[i]!) ? 1 : 0);
      if (!n) continue;
      const end = i + n - 1;
      const before = w.slice(Math.max(0, i - 3), i).filter((b) => b !== 'anti');
      const after = w.slice(end + 1, end + 5);
      const no = before.some((b) => NEGATION.has(b)) || (before.at(-2) === 'w' && before.at(-1) === 'o') || after.some((a) => CANCELLED.test(a)) || saysNoAfter(w, end);
      // one request: what it says once is the answer (a "Watch-it anti collision system" is one)
      if (!no) return [{ qty: 1, model: '' }];
      break;
    }
  }
  return [];
}

/** the boats that take the Watch-it anti-collision system, one each (countWritten with findWatchIts). */
export const countWatchIts = (vessels: ProcVessel[], sheet: SheetData): WrittenCount => countWritten(vessels, sheet, findWatchIts);
